/**
 * Lot P0 #1 (2026-09-09) — Bandeau readiness légale BE (VAT + IBAN).
 *
 * Fetch `GET /api/company/invoice-readiness` (contrat stable :
 * `{ready, missing, invalid, warnings}`) et affiche :
 *   - Bandeau ORANGE si `ready=false` (bloquant émission facture)
 *   - Bandeau JAUNE plus discret si `ready=true` mais `warnings` non-vide
 *   - Rien si tout OK
 *
 * Utilisé par :
 *   - `QuoteEditorPage` (info proactive sur devis signé, `showOnStatus="signe"`)
 *   - `ChantierInvoicesSection` (info + les boutons émission sont disabled côté parent)
 *
 * Rend disponible via prop `onReadinessChange({ready, missing, invalid, warnings})`
 * pour que le parent puisse disable ses boutons d'émission.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Info } from "lucide-react";
import { api } from "@/lib/api";

const FIELD_LABEL = {
  vat_number: "Numéro de TVA",
  iban: "IBAN",
  bic: "BIC",
};

function _labels(fields) {
  return (fields || []).map((f) => FIELD_LABEL[f] || f).join(", ");
}

export default function InvoiceReadinessBanner({ onReadinessChange, className = "" }) {
  const [state, setState] = useState(null); // {ready, missing, invalid, warnings}
  const [error, setError] = useState(false);
  // Ref stable pour éviter la re-fetch en boucle quand le parent passe
  // une nouvelle référence de callback à chaque render.
  const cbRef = useRef(onReadinessChange);
  cbRef.current = onReadinessChange;

  useEffect(() => {
    let cancelled = false;
    api
      .get("/company/invoice-readiness")
      .then((res) => {
        if (cancelled) return;
        setState(res.data);
        if (cbRef.current) cbRef.current(res.data);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        // Fail-open : si l'endpoint tombe, on n'empêche pas l'UI. Le blocage
        // backend reste en place (défense en profondeur).
        if (cbRef.current) cbRef.current({ ready: true, missing: [], invalid: [], warnings: [] });
      });
    return () => { cancelled = true; };
  }, []);

  if (error || !state) return null;

  const { ready, missing, invalid, warnings } = state;

  // Bandeau bloquant (orange)
  if (!ready) {
    return (
      <div
        className={`flex items-start gap-3 rounded border border-[#C4553B]/60 bg-[#C4553B]/10 px-4 py-3 text-sm ${className}`}
        data-testid="invoice-readiness-blocker-banner"
      >
        <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#F0C572]" aria-hidden />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-100">
            Émission facture bloquée — coordonnées entreprise incomplètes
          </p>
          <ul className="mt-1 space-y-0.5 text-xs text-slate-300">
            {missing && missing.length > 0 && (
              <li data-testid="readiness-missing-fields">
                <span className="text-slate-400">Manquant :</span> {_labels(missing)}
              </li>
            )}
            {invalid && invalid.length > 0 && (
              <li data-testid="readiness-invalid-fields">
                <span className="text-slate-400">Format invalide :</span> {_labels(invalid)}
              </li>
            )}
          </ul>
          <Link
            to="/settings/company"
            className="mt-2 inline-block text-xs font-medium text-[#F5C4AC] underline underline-offset-2 hover:text-[#F0C572]"
            data-testid="readiness-cta-complete-settings"
          >
            Compléter les infos entreprise →
          </Link>
        </div>
      </div>
    );
  }

  // Bandeau non-bloquant (jaune)
  if (warnings && warnings.length > 0) {
    return (
      <div
        className={`flex items-start gap-3 rounded border border-slate-700/60 bg-slate-800/40 px-4 py-2.5 text-xs ${className}`}
        data-testid="invoice-readiness-warning-banner"
      >
        <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-400" aria-hidden />
        <div className="flex-1 min-w-0 text-slate-300">
          Recommandé de compléter : <span className="text-slate-200">{_labels(warnings)}</span>.
          {" "}
          <Link
            to="/settings/company"
            className="text-slate-400 underline underline-offset-2 hover:text-slate-200"
            data-testid="readiness-cta-complete-warnings"
          >
            Compléter →
          </Link>
        </div>
      </div>
    );
  }

  return null;
}
